import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  Card,
  Statistic,
  DatePicker,
  Select,
  Button,
  Table,
  Progress,
  Tag,
  Space,
  Input,
  Tooltip,
  message,
  Grid
} from "antd";
import {
  DollarOutlined,
  ShoppingOutlined,
  RiseOutlined,
  CalendarOutlined,
  BookOutlined,
  DownloadOutlined,
  SearchOutlined
} from "@ant-design/icons";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";

import "./style.scss";
import { getAllPayments, getAllCoursesByRole } from "../../../Constants/Api/functions/function";

dayjs.extend(isBetween);

const { RangePicker } = DatePicker;

// Reusable Custom SVG Chart Component matching the design guidelines
const RevenueTrendChart = ({ data, strokeColor, gradientId, gradientColors }) => {
  const [hoveredIndex, setHoveredIndex] = useState(null);

  if (!data || data.length === 0) {
    return (
      <div className="custom-chart-card empty">
        <h3>Revenue Growth Trend</h3>
        <p>No successful payments found in this timeframe.</p>
      </div>
    );
  }

  const width = 600;
  const height = 300;
  const padding = { top: 25, right: 20, bottom: 40, left: 65 };
  const chartWidth = width - padding.left - padding.right;
  const chartHeight = height - padding.top - padding.bottom;
  const maxVal = Math.max(...data.map(d => Number(d.value) || 0), 1000);

  const points = data.map((d, index) => {
    const x = padding.left + (index / Math.max(data.length - 1, 1)) * chartWidth;
    const val = Number(d.value) || 0;
    const yCoord = padding.top + chartHeight - (val / maxVal) * chartHeight;
    return { x, yCoord, value: val, label: d.label };
  });

  let pathD = "";
  let areaD = "";
  if (points.length > 0) {
    pathD = `M ${points[0].x} ${points[0].yCoord}`;
    for (let i = 1; i < points.length; i++) {
      pathD += ` L ${points[i].x} ${points[i].yCoord}`;
    }
    areaD = `${pathD} L ${points[points.length - 1].x} ${padding.top + chartHeight} L ${points[0].x} ${padding.top + chartHeight} Z`;
  }

  const gridLines = [];
  for (let i = 0; i <= 4; i++) {
    const val = (maxVal / 4) * i;
    const yCoord = padding.top + chartHeight - (val / maxVal) * chartHeight;
    gridLines.push({ val, yCoord });
  }

  const formatYValue = (val) => {
    if (val >= 1000000) return `${(val / 1000000).toFixed(1)}M`;
    if (val >= 1000) return `${(val / 1000).toFixed(0)}k`;
    return val;
  };

  return (
    <div className="revenue-trend-chart-card">
      <div className="chart-header">
        <div>
          <h3>Revenue Growth Trend</h3>
          <p className="subtitle">Visual representation of revenue performance</p>
        </div>
        {hoveredIndex !== null ? (
          <div className="chart-tooltip-indicator" style={{ color: strokeColor }}>
            <span className="tooltip-label">{points[hoveredIndex].label}: </span>
            <span className="tooltip-value">LKR {points[hoveredIndex].value.toLocaleString()}</span>
          </div>
        ) : (
          <div className="chart-summary-indicator">
            <span className="tooltip-label">Average: </span>
            <span className="tooltip-value">
              LKR {Math.round(data.reduce((sum, d) => sum + Number(d.value), 0) / data.length).toLocaleString()}
            </span>
          </div>
        )}
      </div>

      <div className="chart-svg-container">
        <svg width="100%" height="100%" viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="xMidYMid meet">
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={gradientColors[0]} stopOpacity="0.4" />
              <stop offset="100%" stopColor={gradientColors[1]} stopOpacity="0.0" />
            </linearGradient>
          </defs>

          {/* Grid lines */}
          {gridLines.map((line, idx) => (
            <g key={idx}>
              <line x1={padding.left} y1={line.yCoord} x2={width - padding.right} y2={line.yCoord} stroke="#f3f4f6" strokeDasharray="4 4" />
              <text x={padding.left - 10} y={line.yCoord + 4} textAnchor="end" fill="#9ca3af" fontSize="10" fontWeight="500">
                {formatYValue(line.val)}
              </text>
            </g>
          ))}

          {/* Base line */}
          <line x1={padding.left} y1={padding.top + chartHeight} x2={width - padding.right} y2={padding.top + chartHeight} stroke="#e5e7eb" />

          {/* Area under the line */}
          {areaD && <path d={areaD} fill={`url(#${gradientId})`} />}

          {/* Core line */}
          {pathD && <path d={pathD} fill="none" stroke={strokeColor} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />}

          {/* Tooltip hover trigger blocks */}
          {points.map((p, idx) => (
            <g key={idx}>
              <text x={p.x} y={padding.top + chartHeight + 18} textAnchor="middle" fill="#9ca3af" fontSize="9" fontWeight="600">
                {p.label}
              </text>
              <circle cx={p.x} cy={p.yCoord} r={hoveredIndex === idx ? 6 : 4} fill={strokeColor} stroke="#ffffff" strokeWidth="2" style={{ transition: "all 0.15s ease-out" }} />
            </g>
          ))}

          {points.map((p, idx) => {
            const colWidth = chartWidth / (points.length - 1 || 1);
            return (
              <rect
                key={idx}
                x={p.x - colWidth / 2}
                y={padding.top}
                width={colWidth}
                height={chartHeight}
                fill="transparent"
                style={{ cursor: "pointer" }}
                onMouseEnter={() => setHoveredIndex(idx)}
                onMouseLeave={() => setHoveredIndex(null)}
              />
            );
          })}
        </svg>
      </div>
    </div>
  );
};

const RevenueReport = () => {
  const userProfile = useSelector((state) => state.userProfile.userProfile);
  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();

  const [loading, setLoading] = useState(false);
  const [payments, setPayments] = useState([]);
  const [courses, setCourses] = useState([]);

  // Filters State
  const [dateRange, setDateRange] = useState([dayjs().subtract(30, "day"), dayjs()]);
  const [selectedCourse, setSelectedCourse] = useState(undefined);
  const [txSearchText, setTxSearchText] = useState("");

  // Monthly Collections State
  const [selectedMonth, setSelectedMonth] = useState(dayjs());
  const [monthlyViewMode, setMonthlyViewMode] = useState("breakdown"); // 'breakdown' or 'matrix'

  // Excel Loading State
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [paymentsList, coursesList] = await Promise.all([
        getAllPayments(),
        getAllCoursesByRole("superadmin", userProfile?.id)
      ]);
      setPayments(paymentsList || []);
      setCourses(coursesList || []);
    } catch (err) {
      console.error("Failed to load revenue details:", err);
      message.error("Failed to fetch payments data.");
    } finally {
      setLoading(false);
    }
  };

  // Filter payments list according to selection criteria
  const getFilteredPayments = () => {
    return payments.filter((p) => {
      // 1. Success/Paid/Completed statuses only
      const status = p.paymentStatus?.toLowerCase();
      const isSuccessful = status === "success" || status === "paid" || status === "completed";
      if (!isSuccessful) return false;

      // 2. Course Filter
      if (selectedCourse && p.courseId !== selectedCourse) return false;

      // 3. Date Range Filter
      if (dateRange && dateRange[0] && dateRange[1]) {
        const payDate = dayjs(p.paymentDate || p.createdAt);
        const start = dateRange[0].startOf("day");
        const end = dateRange[1].endOf("day");
        if (!payDate.isBetween(start, end, null, "[]")) return false;
      }

      return true;
    });
  };

  const filteredPayments = getFilteredPayments();

  // Summary KPIs
  const totalRevenue = filteredPayments.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);
  const totalTransactions = filteredPayments.length;
  const avgTransaction = totalTransactions > 0 ? totalRevenue / totalTransactions : 0;

  // Group Course breakdown stats
  const getCourseBreakdown = () => {
    const courseStats = {};
    
    // Initialize stats for all courses to show 0s if they exist
    courses.forEach(c => {
      courseStats[c.id] = {
        courseId: c.id,
        courseName: c.name,
        sales: 0,
        revenue: 0
      };
    });

    // Aggregate payments
    filteredPayments.forEach((p) => {
      if (!courseStats[p.courseId]) {
        courseStats[p.courseId] = {
          courseId: p.courseId,
          courseName: p.courseName || "Unknown Course",
          sales: 0,
          revenue: 0
        };
      }
      courseStats[p.courseId].sales += 1;
      courseStats[p.courseId].revenue += parseFloat(p.amount || 0);
    });

    return Object.values(courseStats).sort((a, b) => b.revenue - a.revenue);
  };

  const courseBreakdown = getCourseBreakdown();
  const topCourse = courseBreakdown.length > 0 && courseBreakdown[0].revenue > 0 
    ? courseBreakdown[0].courseName 
    : "N/A";

  const getPaymentMethodsBreakdown = () => {
    const methods = {};
    filteredPayments.forEach((p) => {
      const method = p.paymentMethod || "Online Checkout";
      if (!methods[method]) {
        methods[method] = {
          name: method,
          sales: 0,
          revenue: 0
        };
      }
      methods[method].sales += 1;
      methods[method].revenue += parseFloat(p.amount || 0);
    });
    return Object.values(methods).sort((a, b) => b.revenue - a.revenue);
  };

  const paymentMethodsBreakdown = getPaymentMethodsBreakdown();

  // Chart Data Generation (Growth Visuals)
  const getChartData = () => {
    if (!dateRange || !dateRange[0] || !dateRange[1] || filteredPayments.length === 0) return [];

    const start = dateRange[0];
    const end = dateRange[1];
    const diffDays = end.diff(start, "day");

    // Grouping by Date/Month depending on range length
    const formatStr = diffDays <= 45 ? "YYYY-MM-DD" : "MMM YYYY";
    const labelFormatStr = diffDays <= 45 ? "DD MMM" : "MMM YY";
    
    const groups = {};
    
    // Sort payments chronologically first
    const sortedPayments = [...filteredPayments].sort((a, b) => 
      dayjs(a.paymentDate || a.createdAt).unix() - dayjs(b.paymentDate || b.createdAt).unix()
    );

    sortedPayments.forEach((p) => {
      const pDate = dayjs(p.paymentDate || p.createdAt);
      const groupKey = pDate.format(formatStr);
      const label = pDate.format(labelFormatStr);

      if (!groups[groupKey]) {
        groups[groupKey] = {
          key: groupKey,
          label: label,
          value: 0
        };
      }
      groups[groupKey].value += parseFloat(p.amount || 0);
    });

    return Object.values(groups);
  };

  const chartDataPoints = getChartData();

  // Monthly course collections for selected month
  const monthlyCourseCollections = (() => {
    if (!selectedMonth || payments.length === 0) return { list: [], total: 0, count: 0 };
    const targetMonthStr = selectedMonth.format("YYYY-MM");
    const monthPayments = payments.filter((p) => {
      const status = p.paymentStatus?.toLowerCase();
      const isSuccessful = status === "success" || status === "paid" || status === "completed";
      if (!isSuccessful) return false;
      const pDate = p.paymentDate ? dayjs(p.paymentDate) : dayjs(p.createdAt);
      return pDate.format("YYYY-MM") === targetMonthStr;
    });

    const map = {};
    let monthTotal = 0;
    monthPayments.forEach((p) => {
      const cId = p.courseId || "unknown";
      if (!map[cId]) {
        map[cId] = {
          courseId: cId,
          courseName: p.courseName || "General / Other",
          sales: 0,
          collections: 0,
        };
      }
      map[cId].sales += 1;
      const amt = parseFloat(p.amount || 0);
      map[cId].collections += amt;
      monthTotal += amt;
    });

    const list = Object.values(map).sort((a, b) => b.collections - a.collections);
    return { list, total: monthTotal, count: monthPayments.length };
  })();

  // 12-Month Cross-Tab Matrix for selected year
  const yearly12MonthMatrix = (() => {
    if (!selectedMonth || payments.length === 0) return [];
    const targetYear = selectedMonth.year();
    const months = ["01", "02", "03", "04", "05", "06", "07", "08", "09", "10", "11", "12"];

    const map = {};
    payments.forEach((p) => {
      const status = p.paymentStatus?.toLowerCase();
      const isSuccessful = status === "success" || status === "paid" || status === "completed";
      if (!isSuccessful) return;
      const pDate = p.paymentDate ? dayjs(p.paymentDate) : dayjs(p.createdAt);
      if (pDate.year() !== targetYear) return;

      const cId = p.courseId || "unknown";
      if (!map[cId]) {
        map[cId] = {
          courseId: cId,
          courseName: p.courseName || "General / Other",
          total: 0,
        };
        months.forEach((m) => {
          map[cId][`m${m}`] = 0;
        });
      }

      const mStr = pDate.format("MM");
      const amt = parseFloat(p.amount || 0);
      map[cId][`m${mStr}`] += amt;
      map[cId].total += amt;
    });

    return Object.values(map).sort((a, b) => b.total - a.total);
  })();

  // Excel Exporter
  const handleExportExcel = () => {
    if (filteredPayments.length === 0) {
      message.warning("No data available to export in this timeframe.");
      return;
    }
    setIsExporting(true);

    // 1. Prepare Summary Sheet data
    const summarySheetData = courseBreakdown.map((item) => ({
      "Course Name": item.courseName,
      "Enrollments (Sales)": item.sales,
      "Revenue (LKR)": item.revenue,
      "Contribution %": totalRevenue > 0 ? ((item.revenue / totalRevenue) * 100).toFixed(2) + "%" : "0%"
    }));

    // 2. Prepare Transactions Sheet data
    const transactionSheetData = filteredPayments.map((p) => ({
      "Invoice Number": p.invoiceNumber || "N/A",
      "Transaction ID": p.id,
      "Student Number": p.studentNumber || p.indexNumber || "N/A",
      "Student Name": p.userName || "N/A",
      "Email": p.email || "N/A",
      "Course Name": p.courseName || "Unknown Course",
      "Payment Method": p.paymentMethod || "Online Checkout",
      "Amount (LKR)": parseFloat(p.amount || 0),
      "Coupon Code": p.couponCode || "None",
      "Discount (LKR)": parseFloat(p.discountAmount || 0),
      "Payment Date": p.paymentDate ? dayjs(p.paymentDate).format("YYYY-MM-DD HH:mm:ss") : dayjs(p.createdAt).format("YYYY-MM-DD HH:mm:ss"),
      "Status": p.paymentStatus
    }));

    // Dynamic Imports matching project guidelines
    Promise.all([
      import("xlsx"),
      import("file-saver")
    ]).then(([xlsx, FileSaver]) => {
      const wb = xlsx.utils.book_new();

      // Summary sheet
      const wsSummary = xlsx.utils.json_to_sheet(summarySheetData);
      xlsx.utils.book_append_sheet(wb, wsSummary, "Revenue Summary");

      // Transactions sheet
      const wsTx = xlsx.utils.json_to_sheet(transactionSheetData);
      xlsx.utils.book_append_sheet(wb, wsTx, "Transaction Log");

      // 3. Monthly Course Breakdown sheet
      const monthlyExportData = monthlyCourseCollections.list.map((item) => ({
        "Month": selectedMonth.format("MMMM YYYY"),
        "Course Name": item.courseName,
        "Invoices / Sales": item.sales,
        "Collections (LKR)": item.collections,
        "Monthly Share %": monthlyCourseCollections.total > 0
          ? ((item.collections / monthlyCourseCollections.total) * 100).toFixed(2) + "%"
          : "0%"
      }));
      const wsMonthly = xlsx.utils.json_to_sheet(monthlyExportData);
      xlsx.utils.book_append_sheet(wb, wsMonthly, "Monthly Collections");

      // 4. 12-Month Matrix sheet
      const matrixExportData = yearly12MonthMatrix.map((item) => ({
        "Year": selectedMonth.year(),
        "Course Name": item.courseName,
        "Jan": item.m01 || 0,
        "Feb": item.m02 || 0,
        "Mar": item.m03 || 0,
        "Apr": item.m04 || 0,
        "May": item.m05 || 0,
        "Jun": item.m06 || 0,
        "Jul": item.m07 || 0,
        "Aug": item.m08 || 0,
        "Sep": item.m09 || 0,
        "Oct": item.m10 || 0,
        "Nov": item.m11 || 0,
        "Dec": item.m12 || 0,
        "Total Annual Collection (LKR)": item.total || 0,
      }));
      const wsMatrix = xlsx.utils.json_to_sheet(matrixExportData);
      xlsx.utils.book_append_sheet(wb, wsMatrix, `${selectedMonth.year()} 12-Month Matrix`);

      // Write array buffer
      const excelBuffer = xlsx.write(wb, { bookType: "xlsx", type: "array" });
      const EXCEL_TYPE = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
      const blob = new Blob([excelBuffer], { type: EXCEL_TYPE });
      
      const fileName = `Revenue_Report_${dayjs().format("YYYYMMDD_HHmmss")}.xlsx`;
      FileSaver.default.saveAs(blob, fileName);
      
      message.success("Excel report exported successfully!");
    }).catch(err => {
      console.error("Export error", err);
      message.error("Failed to generate Excel report.");
    }).finally(() => {
      setIsExporting(false);
    });
  };

  // Preset Filters
  const handleRangePreset = (days) => {
    setDateRange([dayjs().subtract(days, "day"), dayjs()]);
  };

  // Detailed payments table filtering by search input
  const getSearchedPayments = () => {
    if (!txSearchText.trim()) return filteredPayments;
    const s = txSearchText.toLowerCase();
    return filteredPayments.filter(p => 
      (p.userName || "").toLowerCase().includes(s) ||
      (p.email || "").toLowerCase().includes(s) ||
      (p.courseName || "").toLowerCase().includes(s) ||
      (p.paymentMethod || "").toLowerCase().includes(s)
    );
  };

  const searchedPaymentsList = getSearchedPayments();

  // Columns for course breakdown table
  const breakdownColumns = [
    {
      title: "Course Title",
      dataIndex: "courseName",
      key: "courseName",
      render: (text) => <strong>{text}</strong>
    },
    {
      title: "Completed Sales",
      dataIndex: "sales",
      key: "sales",
      align: "center",
      render: (val) => <Tag color="blue" style={{ fontSize: "12px", padding: "2px 8px" }}>{val} Enrolled</Tag>
    },
    {
      title: "Revenue (LKR)",
      dataIndex: "revenue",
      key: "revenue",
      render: (val) => <span className="revenue-val">{val.toLocaleString()} LKR</span>
    },
    {
      title: "Contribution",
      key: "contribution",
      width: "25%",
      render: (_, record) => {
        const pct = totalRevenue > 0 ? (record.revenue / totalRevenue) * 100 : 0;
        return (
          <div className="progress-cell">
            <Progress percent={parseFloat(pct.toFixed(1))} size="small" strokeColor="#f0b323" />
          </div>
        );
      }
    }
  ];

  // Columns for individual transaction log table
  const txColumns = [
    {
      title: "Invoice No",
      dataIndex: "invoiceNumber",
      key: "invoiceNumber",
      render: (inv) => <Tag color="geekblue" style={{ fontWeight: 600 }}>{inv || "N/A"}</Tag>
    },
    {
      title: "Student Details",
      key: "student",
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 600 }}>{record.userName}</div>
          <div style={{ fontSize: "11px", color: "#666" }}>
            {record.studentNumber || record.indexNumber ? (
              <span style={{ color: "#03245e", fontWeight: 500 }}>
                {record.studentNumber || record.indexNumber} • 
              </span>
            ) : null} {record.email}
          </div>
        </div>
      )
    },
    {
      title: "Course",
      dataIndex: "courseName",
      key: "courseName",
      ellipsis: true
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      render: (amount, record) => (
        <div>
          <strong>{parseFloat(amount).toLocaleString()} LKR</strong>
          {record.couponCode && (
            <div style={{ marginTop: 2 }}>
              <Tag color="cyan" style={{ fontSize: "11px" }}>
                Coupon: {record.couponCode} (-{parseFloat(record.discountAmount || 0).toLocaleString()} LKR)
              </Tag>
            </div>
          )}
        </div>
      )
    },
    {
      title: "Method",
      dataIndex: "paymentMethod",
      key: "paymentMethod",
      render: (method) => <Tag color="geekblue">{method || "Online Checkout"}</Tag>
    },
    {
      title: "Date / Time",
      key: "date",
      render: (_, record) => (
        <span>{dayjs(record.paymentDate || record.createdAt).format("YYYY-MM-DD HH:mm")}</span>
      )
    }
  ];

  // Columns for Monthly Breakdown Table
  const monthlyBreakdownColumns = [
    {
      title: "Course Title",
      dataIndex: "courseName",
      key: "courseName",
      render: (text) => <strong>{text}</strong>
    },
    {
      title: "Enrolled / Invoices",
      dataIndex: "sales",
      key: "sales",
      align: "center",
      render: (val) => <Tag color="cyan">{val} Students</Tag>
    },
    {
      title: "Fee Collected (LKR)",
      dataIndex: "collections",
      key: "collections",
      render: (val) => <span className="revenue-val">{Number(val).toLocaleString()} LKR</span>
    },
    {
      title: "Monthly Share",
      key: "share",
      width: "25%",
      render: (_, record) => {
        const pct = monthlyCourseCollections.total > 0
          ? (record.collections / monthlyCourseCollections.total) * 100
          : 0;
        return (
          <div className="progress-cell">
            <Progress percent={parseFloat(pct.toFixed(1))} size="small" strokeColor="#0284c7" />
          </div>
        );
      }
    }
  ];

  // Columns for 12-Month Matrix Table
  const yearlyMatrixColumns = [
    {
      title: "Course Title",
      dataIndex: "courseName",
      key: "courseName",
      fixed: "left",
      width: 200,
      render: (text) => <strong>{text}</strong>
    },
    ...["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"].map((mName, idx) => {
      const mKey = `m${String(idx + 1).padStart(2, "0")}`;
      return {
        title: mName,
        dataIndex: mKey,
        key: mKey,
        align: "right",
        width: 95,
        render: (val) => (
          <span className={`matrix-cell ${val > 0 ? "positive" : "zero"}`}>
            {val > 0 ? Number(val).toLocaleString() : "-"}
          </span>
        )
      };
    }),
    {
      title: "Total Year (LKR)",
      dataIndex: "total",
      key: "total",
      fixed: "right",
      width: 150,
      render: (val) => <strong style={{ color: "#03245e" }}>{Number(val).toLocaleString()} LKR</strong>
    }
  ];

  return (
    <div className="revenue-report-page">
      <div className="revenue-header">
        <div>
          <h2>System Revenue Analytics</h2>
          <p>Monitor earnings, sales performance, and visual growth trends across all courses.</p>
        </div>
        <Button
          type="primary"
          icon={<DownloadOutlined />}
          onClick={handleExportExcel}
          loading={isExporting}
          className="export-btn-premium"
        >
          Export Excel Report
        </Button>
      </div>

      {/* FILTER CONTROL CARD */}
      <Card className="glass-card filter-card">
        <Space direction={screens.xs ? "vertical" : "horizontal"} size="middle" wrap style={{ width: "100%" }}>
          <div className="filter-group">
            <span className="filter-label"><CalendarOutlined /> Timeframe:</span>
            <RangePicker
              value={dateRange}
              onChange={(val) => setDateRange(val)}
              allowClear={false}
              style={{ borderRadius: "6px" }}
            />
          </div>

          <div className="preset-buttons">
            <Button size="small" onClick={() => handleRangePreset(7)}>7 Days</Button>
            <Button size="small" onClick={() => handleRangePreset(30)}>30 Days</Button>
            <Button size="small" onClick={() => handleRangePreset(180)}>6 Months</Button>
            <Button size="small" onClick={() => setDateRange([dayjs().startOf("year"), dayjs()])}>This Year</Button>
          </div>

          <div className="filter-group" style={{ marginLeft: screens.xs ? 0 : "auto" }}>
            <span className="filter-label"><BookOutlined /> Course Filter:</span>
            <Select
              allowClear
              placeholder="All Courses"
              value={selectedCourse}
              onChange={(val) => setSelectedCourse(val)}
              style={{ width: 220, borderRadius: "6px" }}
              dropdownMatchSelectWidth={false}
            >
              {courses.map((c) => (
                <Select.Option key={c.id} value={c.id}>
                  {c.name}
                </Select.Option>
              ))}
            </Select>
          </div>
        </Space>
      </Card>

      {/* KPI METRIC CARDS */}
      <div className="revenue-kpi-grid">
        <Card className="glass-kpi gold">
          <Statistic
            title="Total Net Revenue"
            value={totalRevenue}
            precision={2}
            suffix="LKR"
            prefix={<DollarOutlined className="kpi-icon" />}
            loading={loading}
          />
        </Card>

        <Card className="glass-kpi blue">
          <Statistic
            title="Successful Registrations"
            value={totalTransactions}
            suffix="Enrollments"
            prefix={<ShoppingOutlined className="kpi-icon" />}
            loading={loading}
          />
        </Card>

        <Card className="glass-kpi purple">
          <Statistic
            title="Top Performing Course"
            value={topCourse}
            formatter={(value) => (
              <Tooltip title={value}>
                <div className="kpi-top-course-text">{value}</div>
              </Tooltip>
            )}
            loading={loading}
          />
        </Card>
      </div>

      {/* GROWTH CHART & PAYMENT METHOD VISUALS */}
      <div className="chart-grid-row">
        <RevenueTrendChart
          data={chartDataPoints}
          strokeColor="#f0b323"
          gradientId="revenueGrad"
          gradientColors={["#f0b323", "#ffffff"]}
        />

        {/* REVENUE BY PAYMENT METHOD */}
        <Card className="glass-card payment-methods-card" title="Revenue by Payment Method" bordered={false}>
          <div className="payment-methods-list">
            {paymentMethodsBreakdown.map((item, idx) => {
              const pct = totalRevenue > 0 ? (item.revenue / totalRevenue) * 100 : 0;
              // Preset colors for payment method indicators
              const colors = ["#1e40af", "#0d9488", "#7c3aed", "#f0b323"];
              const currentColor = colors[idx % colors.length];

              // Map 'paycorp' to 'Card Payment (Sampath Gateway)'
              const displayName = item.name.toLowerCase() === "paycorp"
                ? "Card Payment (Sampath Gateway)"
                : item.name;

              return (
                <div key={item.name} className="payment-method-item">
                  <div className="item-header">
                    <span className="method-name">
                      <span className="color-dot" style={{ backgroundColor: currentColor }}></span>
                      <strong>{displayName}</strong>
                    </span>
                    <span className="method-sales">
                      <Tag color="default">{item.sales} Sales</Tag>
                    </span>
                  </div>
                  <div className="item-body">
                    <div className="item-amount">
                      <span>LKR {item.revenue.toLocaleString()}</span>
                      <span className="pct-label">{pct.toFixed(1)}%</span>
                    </div>
                    <Progress 
                      percent={parseFloat(pct.toFixed(1))} 
                      showInfo={false} 
                      strokeColor={currentColor} 
                      size="small" 
                    />
                  </div>
                </div>
              );
            })}
            {paymentMethodsBreakdown.length === 0 && (
              <p className="empty-text">No payment records available.</p>
            )}
          </div>
        </Card>
      </div>

      {/* BREAKDOWN SECTIONS */}
      <div className="breakdown-grid">
        {/* COURSE BREAKDOWN TABLE */}
        <Card className="glass-card list-card" title="Revenue Share by Course" bordered={false}>
          <Table
            dataSource={courseBreakdown}
            columns={breakdownColumns}
            rowKey="courseId"
            loading={loading}
            pagination={{ pageSize: 5 }}
            className="glass-table"
          />
        </Card>

        {/* DETAILED TRANSACTION LOGS */}
        <Card
          className="glass-card list-card"
          title="Recent Transactions Log"
          bordered={false}
          extra={
            <Input
              placeholder="Search student, course, method..."
              prefix={<SearchOutlined />}
              value={txSearchText}
              onChange={(e) => setTxSearchText(e.target.value)}
              style={{ width: 240, borderRadius: "6px" }}
              allowClear
            />
          }
        >
          <Table
            dataSource={searchedPaymentsList}
            columns={txColumns}
            rowKey="id"
            loading={loading}
            pagination={{ pageSize: 5 }}
            className="glass-table"
            scroll={{ x: "max-content" }}
          />
        </Card>
      </div>

      {/* MONTHLY COURSE COLLECTION REPORT */}
      <Card
        className="glass-card monthly-collection-card"
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <CalendarOutlined style={{ color: "#03245e", fontSize: 18 }} />
            <span>Monthly Course Collection Report</span>
          </div>
        }
        bordered={false}
        extra={
          <div className="monthly-header-actions">
            <DatePicker
              picker="month"
              value={selectedMonth}
              onChange={(val) => val && setSelectedMonth(val)}
              allowClear={false}
              style={{ width: 160 }}
            />
            <Button.Group>
              <Button
                type={monthlyViewMode === "breakdown" ? "primary" : "default"}
                onClick={() => setMonthlyViewMode("breakdown")}
              >
                Month Details
              </Button>
              <Button
                type={monthlyViewMode === "matrix" ? "primary" : "default"}
                onClick={() => setMonthlyViewMode("matrix")}
              >
                12-Month Matrix ({selectedMonth.year()})
              </Button>
            </Button.Group>
          </div>
        }
      >
        <div className="month-kpis">
          <div className="month-kpi-chip">
            <span className="label">Total Collected ({selectedMonth.format("MMM YYYY")})</span>
            <span className="value">LKR {monthlyCourseCollections.total.toLocaleString()}</span>
          </div>
          <div className="month-kpi-chip">
            <span className="label">Completed Enrolled Payments</span>
            <span className="value">{monthlyCourseCollections.count}</span>
          </div>
          <div className="month-kpi-chip">
            <span className="label">Courses with Revenue</span>
            <span className="value">{monthlyCourseCollections.list.length}</span>
          </div>
        </div>

        {monthlyViewMode === "breakdown" ? (
          <Table
            dataSource={monthlyCourseCollections.list}
            columns={monthlyBreakdownColumns}
            rowKey="courseId"
            loading={loading}
            pagination={{ pageSize: 8 }}
            className="glass-table"
          />
        ) : (
          <Table
            dataSource={yearly12MonthMatrix}
            columns={yearlyMatrixColumns}
            rowKey="courseId"
            loading={loading}
            pagination={{ pageSize: 8 }}
            className="glass-table"
            scroll={{ x: 1400 }}
          />
        )}
      </Card>
    </div>
  );
};

export default RevenueReport;
